import { deserializeTrigger } from '/src/features/goals/machines/wizard';
import { pickFields } from '/src/utils';
import { RECORDINGS_FILTER_VERSION } from '/src/features/recordings/constants';
import { SPECIAL_DATE_RANGES, LOGICALS } from '/src/features/_global/constants';
import { GOAL_TRIGGER_TYPES, ECOMMERCE_EVENTS_LIST_TYPE, GOAL_VALUE_TYPES } from '/src/features/goals/constants';
import { safeJsonParse } from '../_global/utils';

export const firstConditionFromFilter = (filter) => {
  const parsedFilter = safeJsonParse(filter);
  if (!Array.isArray(parsedFilter?.conditions) || parsedFilter.conditions.length === 0) return null;
  return parsedFilter.conditions[0];
};

export const normalizeTriggerType = (triggerType) => {
  if (typeof triggerType !== 'string') return '';
  return triggerType.toUpperCase();
};

const buildTriggerPreviewFromFilter = (trigger, thumbnailUrl = null) => {
  const condition = firstConditionFromFilter(trigger?.filter);
  if (!condition) return null;

  const triggerType = normalizeTriggerType(trigger?.type ?? trigger?.trigger ?? condition.criteria);
  if (![GOAL_TRIGGER_TYPES.CLICK, GOAL_TRIGGER_TYPES.FORM_SUBMISSION].includes(triggerType)) {
    return null;
  }

  const values = Array.isArray(condition?.values) ? condition.values : [];
  const firstValue = values[0];
  const candidatePayload = firstValue?.value ?? firstValue;
  const valuePayload =
    candidatePayload && typeof candidatePayload === 'object' && !Array.isArray(candidatePayload)
      ? candidatePayload
      : {};
  const thumbnail =
    thumbnailUrl ??
    valuePayload?.thumbnail ??
    valuePayload?.thumbnail_for_desktop ??
    valuePayload?.thumbnail_for_phone ??
    valuePayload?.thumbnail_for_tablet ??
    null;

  return {
    type: 'editor',
    thumbnail,
  };
};

export function parseTriggers(triggers) {
  return triggers.map((trigger, index) => {
    const { __typename, ...payload } = trigger;
    return {
      ...payload,
      ...deserializeTrigger(trigger.filter),
      name: trigger?.name ?? '',
      expanded: index === 0,
      valueType: trigger.valueType || GOAL_VALUE_TYPES.NO_VALUE,
      elementValue: trigger.elementValue
        ? {
            ...trigger.elementValue,
            syncSelectors: true,
          }
        : null,
    };
  });
}

const escapeHtml = (unsafe) => {
  return unsafe
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
};

export function generateCodeGoalScript(trigger, code, escaped = true) {
  if (
    trigger.valueType?.toUpperCase() === GOAL_VALUE_TYPES.CODE &&
    trigger.trigger?.toUpperCase() === GOAL_TRIGGER_TYPES.CODE
  ) {
    const baseScript = `<script type="text/javascript">\n\t(window.CE_API || (window.CE_API=[])).push(function(){\n\t\tCE2.converted("${code}", {\n\t\t\tworth: '99.95',\n\t\t\tcurrency: 'USD'\n\t\t});\n\t});\n</script>`;
    return escaped ? escapeHtml(baseScript) : baseScript;
  }

  const baseScript = `<script type="text/javascript">\n\t(window.CE_API || (window.CE_API=[])).push(function(){\n\t\tCE2.converted("${code}");\n\t});\n</script>`;
  return escaped ? escapeHtml(baseScript) : baseScript;
}

export function generateEcommerceGoalScript(type) {
  if (type === ECOMMERCE_EVENTS_LIST_TYPE.ADD_TO_CART) {
    return `&lt;script type=&quot;text/javascript&quot;&gt;
      // Call this code when someone adds something to the shopping cart
      (window.CE_API || (window.CE_API=[])).push(function(){
        CE2.addToCart(
          {
            name: 'Product 1',  // Required.
            price: 99.95,       // Required. Price in default or specified currency.
                                //  This is the total price for _all_ items
                                //  not an individual per-item price.
            currency: 'USD',    // Optional. Three-digit currency code.
            quantity: 2,        // Optional. Defaults to &quot;1&quot; if not provided.
            productId: '12345'  // Optional.
          }
        );
      });
    &lt;/script&gt;`;
  }

  if (type === ECOMMERCE_EVENTS_LIST_TYPE.START_CHECKOUT) {
    return `&lt;script type=&quot;text/javascript&quot;&gt;
      // Call this code when someone *starts* the checkout process
      (window.CE_API || (window.CE_API=[])).push(function(){
        CE2.startCheckout();
      });
    &lt;/script&gt;`;
  }

  if (type === ECOMMERCE_EVENTS_LIST_TYPE.COMPLETE_CHECKOUT) {
    return `&lt;script type=&quot;text/javascript&quot;&gt;
      // Call this code when someone *completed* the checkout process
      // and places an order
      (window.CE_API || (window.CE_API=[])).push(function(){
        CE2.checkoutCompleted({
          total: 99.95,      // Required. The final amount when checking out
                            //   in default or specified currency.
          currency: 'USD',   // Optional. Three-digit currency code.
          orderId: '4444'    // Optional.
        });
      });
    &lt;/script&gt;`;
  }

  return null;
}

export function generateRecordingsFilterUrlForGoal(data, selected = { special: SPECIAL_DATE_RANGES.LAST_7_DAYS }) {
  return JSON.stringify({
    version: RECORDINGS_FILTER_VERSION,
    operator: LOGICALS.AND,
    conditions: [
      {
        criteria: 'goal',
        comparison: 'did all of',
        values: [data.id],
      },
      {
        criteria: 'date_range',
        value: selected.special
          ? {
              special: selected.special,
            }
          : {
              start_date: selected.startDate,
              end_date: selected.endDate,
            },
      },
    ],
  });
}

const basicFields = ['autoName', 'devices', 'expanded', 'filter', 'name', 'syncSelectors', 'trigger'];
const elementFields = [
  'phoneSelector',
  'tabletSelector',
  'desktopSelector',
  'targetUrl',
  'trackingOption',
  'elementSelectorType',
  'wildcardUrl',
];

const getWhitelistedFields = (type) => {
  const normalizedType = normalizeTriggerType(type);

  switch (normalizedType) {
    case GOAL_TRIGGER_TYPES.CODE:
      return [...basicFields, 'code'];
    case GOAL_TRIGGER_TYPES.PIXEL_EVENT:
      return [...basicFields, 'pixelApi', 'pixelCustomEvent', 'pixelEvent', 'pixelId'];
    case GOAL_TRIGGER_TYPES.ECOMMERCE_EVENT:
      return [...basicFields, 'ecommerceApi', 'ecommerceEvent'];
    case GOAL_TRIGGER_TYPES.CLICK:
    case GOAL_TRIGGER_TYPES.FORM_SUBMISSION:
      return [...basicFields, ...elementFields];
    case GOAL_TRIGGER_TYPES.URL_VISIT:
      return [...basicFields, 'targetUrl'];
    default:
      return basicFields;
  }
};

export function contextToArgs(context) {
  const { payload } = context;

  const triggers = payload.triggers.map((t) => pickFields(t, getWhitelistedFields(t.trigger)));

  return {
    ...payload,
    triggers,
  };
}

export function combineTriggersWithSegments(goal, countsData) {
  const triggers = goal?.triggers ?? [];
  const countsById = new Map();
  const thumbnailUrl = goal?.thumbnailUrl;

  (countsData ?? []).forEach((item) => {
    if (!countsById.has(item.id)) {
      countsById.set(item.id, item);
    }
  });

  const result = triggers.map((trigger, i) => {
    const foundTriggerCounts = countsById.get(trigger.id) ?? {};

    return {
      ...trigger,
      preview: buildTriggerPreviewFromFilter(trigger, thumbnailUrl),
      counts: { ...foundTriggerCounts },
      position: i,
    };
  });

  return result.filter((trigger) => {
    // only show deleted triggers with data
    return !trigger.deletedAt || trigger.counts?.conversionsCount > 0;
  });
}

export function getGoalTriggerType(goal) {
  const activeTriggers = Array.isArray(goal?.triggers) ? goal.triggers.filter((trigger) => !trigger?.deletedAt) : [];

  if (!activeTriggers.length) {
    return 'OTHER';
  }

  const criteria = activeTriggers.flatMap((trigger) => {
    const conditions = safeJsonParse(trigger?.filter)?.conditions;

    if (Array.isArray(conditions) && conditions.length > 0) {
      return conditions
        .map((condition) => condition?.criteria)
        .filter((value) => typeof value === 'string' && value.trim() !== '')
        .map((value) => value.trim().toLowerCase());
    }

    return [trigger?.type, trigger?.trigger]
      .filter((value) => typeof value === 'string' && value.trim() !== '')
      .map((value) => value.trim().toLowerCase());
  });

  const uniqueCriteria = [...new Set(criteria)];

  if (uniqueCriteria.length !== 1) {
    return 'OTHER';
  }

  const [singleCriteria] = uniqueCriteria;
  const matchingType = Object.values(GOAL_TRIGGER_TYPES).find((type) => type.toLowerCase() === singleCriteria);

  return matchingType ?? 'OTHER';
}
